import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogOut, BookOpen, Calendar, User, Microscope } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, batchesRes, enrollmentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/courses?is_active=true`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/batches?is_active=true`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/batches/my/enrollments`, { withCredentials: true })
      ]);
      setCourses(coursesRes.data);
      setBatches(batchesRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const enrollInBatch = async (batchId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/batches/${batchId}/enroll`, {}, { withCredentials: true });
      toast.success('Successfully enrolled!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enrollment failed');
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.course_id === courseId);
    return course ? course.name : 'Unknown Course';
  };

  const isEnrolled = (batchId) => {
    return enrollments.some(e => e.batch_id === batchId && e.status === 'active');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-2 rounded-lg">
                <Microscope className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-purple-900">Tejeswi Student Portal</h1>
                <p className="text-xs text-purple-600">Chemistry Education</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                <p className="text-xs text-purple-600">Student</p>
              </div>
              <Button onClick={handleLogout} variant="outline" size="sm" className="border-purple-200">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <Card className="border-purple-200 mb-8 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
          <CardHeader>
            <CardTitle className="text-2xl">Welcome back, {user?.name?.split(' ')[0]}!</CardTitle>
            <CardDescription className="text-purple-100">
              Continue your chemistry learning journey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-3xl font-bold">{enrollments.length}</p>
                <p className="text-purple-200 text-sm">Enrolled Batches</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{courses.length}</p>
                <p className="text-purple-200 text-sm">Available Courses</p>
              </div>
              <div>
                <p className="text-3xl font-bold">{batches.length}</p>
                <p className="text-purple-200 text-sm">Active Batches</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* My Enrollments */}
        {enrollments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">My Enrolled Batches</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {enrollments.map((enrollment) => {
                const batch = batches.find(b => b.batch_id === enrollment.batch_id);
                if (!batch) return null;
                return (
                  <Card key={enrollment.enrollment_id} className="border-green-200 bg-green-50">
                    <CardHeader>
                      <CardTitle className="text-lg">{batch.batch_name}</CardTitle>
                      <CardDescription>{getCourseName(batch.course_id)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar className="h-4 w-4 mr-2" />
                        {batch.schedule}
                      </div>
                      <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">Enrolled</span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Courses */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Courses</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {courses.map((course) => (
              <Card key={course.course_id} className="border-purple-200">
                <CardHeader>
                  <CardTitle className="text-lg">{course.name}</CardTitle>
                  <CardDescription>{course.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-3">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">{course.curriculum}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Grade {course.grade}</span>
                  </div>
                  <Button
                    onClick={() => {
                      const courseBatches = batches.filter(b => b.course_id === course.course_id);
                      if (courseBatches.length > 0) {
                        document.getElementById('available-batches').scrollIntoView({ behavior: 'smooth' });
                      } else {
                        toast.info('No batches available for this course yet');
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    View Batches
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Available Batches */}
        <div id="available-batches">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Batches</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {batches.map((batch) => (
              <Card key={batch.batch_id} className="border-purple-200">
                <CardHeader>
                  <CardTitle className="text-lg">{batch.batch_name}</CardTitle>
                  <CardDescription>{getCourseName(batch.course_id)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      {batch.schedule}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="h-4 w-4 mr-2" />
                      {batch.enrolled_count}/{batch.capacity} students
                    </div>
                  </div>
                  {isEnrolled(batch.batch_id) ? (
                    <Button disabled className="w-full bg-green-600 text-white">
                      Already Enrolled
                    </Button>
                  ) : batch.enrolled_count >= batch.capacity ? (
                    <Button disabled className="w-full">
                      Batch Full
                    </Button>
                  ) : (
                    <Button
                      onClick={() => enrollInBatch(batch.batch_id)}
                      className="w-full bg-purple-700 hover:bg-purple-800"
                    >
                      Enroll Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {batches.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No batches available at the moment. Check back later!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
